import PropTypes from 'prop-types';
import { useState } from 'react';
import { useI18n } from '../../../../../context/I18nContext';
import {
  POWER_LEVEL_OPTIONS,
  PERMISSION_EVENTS,
  PERMISSION_LABELS,
  POWER_LEVELS,
} from '../../../../../constants/roomSettings';
import './PermissionsPage.css';

const PermissionRow = ({
  label,
  value,
  onChange,
  canConfigure,
  permissionKey,
}) => {
  const { t } = useI18n();
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    
    if (newValue === POWER_LEVELS.CUSTOM) {
      setShowCustomInput(true);
      setCustomValue(value.toString());
    } else {
      setShowCustomInput(false);
      onChange(newValue);
    }
  };

  const handleCustomSubmit = () => {
    const numValue = parseInt(customValue, 10);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      onChange(numValue);
      setShowCustomInput(false);
    }
  };

  const handleCustomValueChange = (e) => {
    const input = e.target.value;
    // Allow empty string for clearing
    if (input === '') {
      setCustomValue('');
      return;
    }
    
    const numValue = parseInt(input, 10);
    // Only update if valid number and within range
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setCustomValue(input);
    }
    // Ignore invalid input (do not update state)
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomSubmit();
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const handleCustomValueClick = () => {
    if (canConfigure) {
      setShowCustomInput(true);
      setCustomValue(value.toString());
    }
  };

  // Check if current value is a custom (non-standard) value
  const isCustomValue = !POWER_LEVEL_OPTIONS.some(opt => opt.value === value && opt.value !== POWER_LEVELS.CUSTOM);

  // Determine which option should be selected
  const getSelectedValue = () => {
    if (showCustomInput) return POWER_LEVELS.CUSTOM;
    const matchingOption = POWER_LEVEL_OPTIONS.find(opt => opt.value === value && opt.value !== POWER_LEVELS.CUSTOM);
    return matchingOption ? value : POWER_LEVELS.CUSTOM;
  };

  return (
    <div className="permission-row">
      <span className="permission-label">{t(label)}</span>
      <div className="permission-control">
        <select
          id={`room-settings-permission-${permissionKey}`}
          className="permission-select"
          value={getSelectedValue()}
          onChange={handleChange}
          disabled={!canConfigure}
        >
          {POWER_LEVEL_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
        {showCustomInput && (
          <div className="permission-custom-input-container">
            <input
              id={`room-settings-permission-custom-${permissionKey}`}
              type="number"
              className="permission-custom-input"
              value={customValue}
              onChange={handleCustomValueChange}
              onKeyDown={handleCustomKeyPress}
              onBlur={handleCustomSubmit}
              placeholder={t('RoomSettings.customLevelPlaceholder')}
              min="0"
              max="100"
              autoFocus
            />
          </div>
        )}
        {!showCustomInput && isCustomValue && (
          <span
            className={`permission-custom-value ${canConfigure ? 'permission-custom-value-clickable' : ''}`}
            onClick={handleCustomValueClick}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && canConfigure && handleCustomValueClick()}
            role={canConfigure ? 'button' : undefined}
            tabIndex={canConfigure ? 0 : -1}
            title={canConfigure ? t('RoomSettings.customLevelClickToEdit') : ''}
          >
            ({value})
          </span>
        )}
      </div>
    </div>
  );
};

PermissionRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  canConfigure: PropTypes.bool,
  permissionKey: PropTypes.string.isRequired,
};

const PermissionsPage = ({
  permissionLevels = {},
  onPermissionChange = () => {},
  canConfigure = false,
}) => {
  const { t } = useI18n();
  const permissionKeys = Object.keys(PERMISSION_EVENTS);

  return (
    <div className="room-settings-page permissions-page">
      <div className="room-settings-section">
        <div className="room-settings-section-header">
          <h3 className="room-settings-section-title">{t('RoomSettings.permissionsTitle')}</h3>
          <p className="room-settings-description">
            {t('RoomSettings.permissionsDescription')}
          </p>
          <div className="room-settings-section-divider" />
        </div>
        <div className="permissions-list">
          {permissionKeys.map((key) => {
            const permissionKey = PERMISSION_EVENTS[key];
            const label = PERMISSION_LABELS[permissionKey];
            const value = permissionLevels[permissionKey] ?? 0;

            return (
              <PermissionRow
                key={permissionKey}
                permissionKey={permissionKey}
                label={label}
                value={value}
                onChange={(newValue) => onPermissionChange(permissionKey, newValue)}
                canConfigure={canConfigure}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

PermissionsPage.propTypes = {
  permissionLevels: PropTypes.object,
  onPermissionChange: PropTypes.func,
  canConfigure: PropTypes.bool,
};

export default PermissionsPage;
